import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { Save, KeyRound, Cake, Utensils } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/authService';
import { setUser } from '@/redux/slices/authSlice';
import { fmtDate } from '@/utils/format';

export default function Profile() {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const profileForm = useForm({
    defaultValues: {
      name: user?.name || '',
      phone: user?.phone || '',
      address: user?.address || '',
      designation: user?.designation || '',
      department: user?.department || '',
      dob: user?.dob ? dayjs(user.dob).format('YYYY-MM-DD') : '',
    },
  });
  const pwdForm = useForm();

  const lunchForm = useForm({
    defaultValues: {
      autoLunch: user?.autoLunch !== false,
      lunchStart: user?.lunchStart || '14:00',
      lunchEnd: user?.lunchEnd || '15:00',
    },
  });

  const saveProfile = async (data) => {
    try {
      const payload = { ...data };
      if (!payload.dob) delete payload.dob;
      const res = await authService.updateProfile(payload);
      dispatch(setUser(res.user));
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const saveLunch = async (data) => {
    if (data.lunchStart >= data.lunchEnd) return toast.error('Lunch start must be before end');
    try {
      const res = await authService.updateProfile({
        autoLunch: !!data.autoLunch,
        lunchStart: data.lunchStart,
        lunchEnd: data.lunchEnd,
      });
      dispatch(setUser(res.user));
      toast.success('Lunch settings saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const changePwd = async (data) => {
    if (data.newPassword !== data.confirm) return toast.error('Passwords do not match');
    try {
      await authService.changePassword({ currentPassword: data.currentPassword, newPassword: data.newPassword });
      toast.success('Password changed');
      pwdForm.reset();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Profile" subtitle="Manage your personal info and password" />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card>
          <div className="flex flex-col items-center text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-500 to-indigo-600 text-white grid place-items-center text-2xl font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="mt-3 font-semibold">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.designation}</div>
            <div className="text-xs text-slate-400 mt-1">{user?.email}</div>
            <div className="mt-4 text-xs grid grid-cols-2 gap-2 w-full">
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2">
                <div className="text-slate-500">Role</div>
                <div className="font-semibold capitalize">{user?.role}</div>
              </div>
              <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2">
                <div className="text-slate-500">ID</div>
                <div className="font-semibold">{user?.employeeId || '—'}</div>
              </div>
            </div>
            {user?.dob && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-500">
                <Cake size={12} /> Birthday: {fmtDate(user.dob, 'DD MMMM')}
              </div>
            )}
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <div className="font-semibold mb-3">Personal information</div>
          <form onSubmit={profileForm.handleSubmit(saveProfile)} className="grid sm:grid-cols-2 gap-3">
            <div><label className="label">Name</label><input className="input" {...profileForm.register('name', { required: true })} /></div>
            <div><label className="label">Phone</label><input className="input" {...profileForm.register('phone')} /></div>
            <div><label className="label">Designation</label><input className="input" {...profileForm.register('designation')} /></div>
            <div><label className="label">Department</label><input className="input" {...profileForm.register('department')} /></div>
            <div>
              <label className="label flex items-center gap-1"><Cake size={12} /> Date of birth</label>
              <input type="date" className="input" {...profileForm.register('dob')} />
              <p className="text-xs text-slate-400 mt-1">Your teammates will be reminded to wish you 🎂</p>
            </div>
            <div className="sm:col-span-2"><label className="label">Address</label><textarea rows={2} className="input" {...profileForm.register('address')} /></div>
            <div className="sm:col-span-2 flex justify-end">
              <button className="btn-primary" type="submit"><Save size={16} /> Save changes</button>
            </div>
          </form>
        </Card>
      </div>

      <Card>
        <div className="font-semibold mb-1 flex items-center gap-2"><Utensils size={16} /> Lunch break</div>
        <p className="text-xs text-slate-500 mb-3">
          Your lunch turns on automatically in this window. If you don't end it yourself, it ends
          automatically once you're active again (or when you check out).
        </p>
        <form onSubmit={lunchForm.handleSubmit(saveLunch)} className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" className="rounded" {...lunchForm.register('autoLunch')} />
            Auto lunch on
          </label>
          <div>
            <label className="label">Starts at</label>
            <input type="time" className="input w-36" {...lunchForm.register('lunchStart', { required: true })} />
          </div>
          <div>
            <label className="label">Ends at</label>
            <input type="time" className="input w-36" {...lunchForm.register('lunchEnd', { required: true })} />
          </div>
          <button className="btn-primary" type="submit"><Save size={16} /> Save</button>
        </form>
      </Card>

      <Card>
        <div className="font-semibold mb-3 flex items-center gap-2"><KeyRound size={16} /> Change password</div>
        <form onSubmit={pwdForm.handleSubmit(changePwd)} className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="label">Current password</label>
            <input type="password" className="input" {...pwdForm.register('currentPassword', { required: true })} />
          </div>
          <div>
            <label className="label">New password</label>
            <input type="password" className="input" {...pwdForm.register('newPassword', { required: true, minLength: 6 })} />
          </div>
          <div>
            <label className="label">Confirm new password</label>
            <input type="password" className="input" {...pwdForm.register('confirm', { required: true })} />
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <button className="btn-primary" type="submit">Change password</button>
          </div>
        </form>
      </Card>
    </div>
  );
}
